import FileOpenOutlinedIcon from "@mui/icons-material/FileOpenOutlined";
import { Tooltip } from "@mui/material";
import StyledButton from "components/shared/StyledButton";
import { SmallBody } from "helpers/TypographyComponents";
import { formatLocaleDateString } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { Agreement } from "store/agreementsStore";
import { AgreementSignatureStatus } from "store/agreementsStore";

import styles from "./style.module.scss";

interface IAgreementStatus {
    /**
     * Handle download agreement PDF
     */
    handleDownload: () => void;
    /**
     * Agreement data
     */
    agreement: Agreement;
}
/**
 * Render Agreement Status component. Show status and action to naviguate to the document
 */
const AgreementStatus = ({ handleDownload, agreement }: IAgreementStatus) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const isSigned = agreement?.signedStatus === AgreementSignatureStatus.SIGNED;

    const handleAgreementNavigate = () => {
        navigate(`/agreements/${agreement?.id}`);
    };

    const tooltipTitle = {
        draft: t("ShowDetails.DownloadContractDraft"),
        sent: t("ShowDetails.DownloadContract"),
        negotiating: t("ShowDetails.DownloadContract"),
        [AgreementSignatureStatus.SIGNED]: t("ShowDetails.DownloadSignedContract"),
    };

    return (
        <div className={styles["term-detail-agreement"]}>
            <div onClick={handleDownload}>
                {!isSigned && (
                    <>
                        {agreement.status === "negotiating" && (
                            <SmallBody color="info">
                                {t("ShowDetails.SentForValidationOn")}{" "}
                                {formatLocaleDateString({
                                    date: agreement.lastUpdate,
                                    options: { numeric: true, timeZone: "UTC" },
                                })}
                            </SmallBody>
                        )}
                        {agreement.status === "draft" && (
                            <SmallBody color="warning">
                                {t("ShowDetails.DraftCreatedOn")}{" "}
                                {formatLocaleDateString({
                                    date: agreement.created,
                                    options: { numeric: true, timeZone: "UTC" },
                                })}
                            </SmallBody>
                        )}
                        {agreement.status === "sent" && (
                            <SmallBody color="info">
                                {t("ShowDetails.SentForSignatureOn")}{" "}
                                {formatLocaleDateString({
                                    date: agreement.lastUpdate,
                                    options: { numeric: true, timeZone: "UTC" },
                                })}
                            </SmallBody>
                        )}
                    </>
                )}
                {isSigned && (
                    <SmallBody color="success">
                        {t("ShowDetails.SignedOn")}{" "}
                        {formatLocaleDateString({
                            date: agreement.lastUpdate,
                            options: { numeric: true, timeZone: "UTC" },
                        })}
                    </SmallBody>
                )}
                <div className={styles["agreement-pdf"]}>
                    <Tooltip title={tooltipTitle[agreement.signedStatus] || tooltipTitle[agreement.status]}>
                        <span>{agreement.filename?.split(".")[0]}</span>
                    </Tooltip>
                </div>
            </div>
            <StyledButton
                buttonType="secondary"
                onClick={handleAgreementNavigate}
                title={t("ShowDetails.NavigateToContract")}
                sx={{ width: "max-content" }}
            >
                <FileOpenOutlinedIcon sx={{ height: "16px", width: "16px" }} />
                {t("ShowDetails.AccessContract")}
            </StyledButton>
        </div>
    );
};

export default AgreementStatus;
